<template>
  <section class="qr-panel" :aria-busy="loading">
    <!-- Pink accent bar — V2 panel signature -->
    <span class="qr-panel__accent" aria-hidden="true" />

    <header class="qr-panel__header">
      <span class="qr-panel__eyebrow">
        <span class="qr-panel__eyebrow-pulse" aria-hidden="true" />
        Broadcast · Scan to donate
      </span>
      <h3 class="qr-panel__title">Your campaign QR code</h3>
      <p class="qr-panel__desc">
        Drop this on flyers, jersey banners, post-game programs, or anywhere your campaign is announced in person.
        Every scan lands a donor on your page — and we'll count it here.
      </p>
    </header>

    <div class="qr-panel__body">
      <!-- LEFT: QR preview card -->
      <div class="qr-panel__preview" v-html="previewSvg || qrPlaceholder" :aria-label="`QR code for ${campaignName}`" role="img" />

      <!-- RIGHT: stats + actions -->
      <div class="qr-panel__data">
        <div class="qr-panel__stat">
          <span class="qr-panel__stat-label">Total scans</span>
          <span class="qr-panel__stat-value">
            <span class="qr-panel__stat-number">{{ animatedTotal.toLocaleString() }}</span>
            <span class="qr-panel__stat-unit" v-if="total === 1">scan</span>
            <span class="qr-panel__stat-unit" v-else>scans</span>
          </span>
          <div class="qr-panel__trend" aria-hidden="true">
            <svg
              class="qr-panel__sparkline"
              :viewBox="`0 0 ${sparkWidth} ${sparkHeight}`"
              preserveAspectRatio="none"
            >
              <!-- Soft pink area beneath the line — gives the sparkline
                   visual weight at low values without screaming. -->
              <path
                v-if="sparkAreaPath"
                :d="sparkAreaPath"
                fill="url(#qrSparkFill)"
                stroke="none"
              />
              <path
                v-if="sparkPath"
                :d="sparkPath"
                fill="none"
                stroke="var(--sideout-pink, #DA1884)"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="qr-panel__sparkline-stroke"
              />
              <defs>
                <linearGradient id="qrSparkFill" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stop-color="#DA1884" stop-opacity="0.18" />
                  <stop offset="100%" stop-color="#DA1884" stop-opacity="0" />
                </linearGradient>
              </defs>
            </svg>
            <span class="qr-panel__trend-caption">
              {{ trendCaption }}
            </span>
          </div>
        </div>

        <div class="qr-panel__actions">
          <button type="button" class="qr-panel__btn qr-panel__btn--primary" @click="downloadPng" :disabled="downloading">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            <span>{{ downloading ? 'Preparing…' : 'Download PNG' }}</span>
          </button>

          <button type="button" class="qr-panel__btn qr-panel__btn--secondary" @click="downloadSvg">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M12 3v12"/>
              <path d="M7 10l5 5 5-5"/>
              <path d="M5 21h14"/>
            </svg>
            <span>Download SVG</span>
          </button>

          <button
            type="button"
            class="qr-panel__btn qr-panel__btn--secondary"
            :class="{ 'is-copied': justCopied }"
            @click="copyLink"
          >
            <svg v-if="!justCopied" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <rect x="9" y="9" width="13" height="13" rx="2"/>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
            </svg>
            <svg v-else viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <span>{{ justCopied ? 'Copied!' : 'Copy share link' }}</span>
          </button>
        </div>

        <ul class="qr-panel__channels" aria-label="Suggested use cases">
          <li v-for="ch in channels" :key="ch">{{ ch }}</li>
        </ul>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'CampaignQrPanel',
  props: {
    campaign: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      total: 0,
      animatedTotal: 0,
      trend: [],
      qrUrl: '',
      shareUrl: '',
      previewSvg: '',
      qrPlaceholder:
        '<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:#9CA3AF;font-size:11px;letter-spacing:0.14em;text-transform:uppercase;">Generating…</div>',
      downloading: false,
      justCopied: false,
      _copyTimeout: null,
      _animFrame: null,
      sparkWidth: 220,
      sparkHeight: 40,
      channels: ['Flyers', 'Jersey banners', 'Program inserts', 'Event signage', 'Email signatures'],
    }
  },
  computed: {
    campaignName() {
      return this.campaign?.name || 'this campaign'
    },
    trendCaption() {
      const last7 = this.trend.reduce((sum, d) => sum + (d.count || 0), 0)
      if (!this.total) {
        return 'Share the code to start tracking scans'
      }
      if (last7 === 0) {
        return 'No scans in the past 7 days — push it back out there'
      }
      if (last7 === 1) {
        return '1 scan in the past 7 days'
      }
      return `${last7.toLocaleString()} scans in the past 7 days`
    },
    sparkPoints() {
      // Always render 7 points. If max=0 the line sits along the bottom
      // (visually grounded) instead of folding to a weird midpoint.
      if (!Array.isArray(this.trend) || this.trend.length === 0) return []
      const max = Math.max(1, ...this.trend.map(d => d.count || 0))
      const stepX = this.sparkWidth / (this.trend.length - 1 || 1)
      // padding so the stroke doesn't clip on min/max values
      const padY = 4
      const usableH = this.sparkHeight - padY * 2
      return this.trend.map((d, i) => {
        const x = i * stepX
        const ratio = (d.count || 0) / max
        const y = this.sparkHeight - padY - ratio * usableH
        return { x, y }
      })
    },
    sparkPath() {
      const pts = this.sparkPoints
      if (pts.length < 2) return ''
      return pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ')
    },
    sparkAreaPath() {
      const pts = this.sparkPoints
      if (pts.length < 2) return ''
      const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ')
      return `${line} L ${this.sparkWidth} ${this.sparkHeight} L 0 ${this.sparkHeight} Z`
    },
  },
  mounted() {
    this.loadStats()
  },
  beforeUnmount() {
    cancelAnimationFrame(this._animFrame)
    clearTimeout(this._copyTimeout)
  },
  methods: {
    async loadStats() {
      this.loading = true
      try {
        const { data } = await axios.get(`/campaign/${this.campaign.id}/qr-stats`)
        this.total = data.total || 0
        this.trend = data.trend || []
        this.qrUrl = data.qr_url
        this.shareUrl = data.share_url
        await this.generatePreview()
        this.animateCount(this.total)
      } catch (err) {
        console.error('CampaignQrPanel: failed to load stats', err)
      } finally {
        this.loading = false
      }
    },
    async generatePreview() {
      if (!this.qrUrl) return
      try {
        const QRCode = (await import('qrcode')).default
        this.previewSvg = await QRCode.toString(this.qrUrl, {
          type: 'svg',
          errorCorrectionLevel: 'M',
          margin: 1,
          width: 240,
          color: { dark: '#1D2549', light: '#ffffff' },
        })
      } catch (err) {
        console.error('CampaignQrPanel: QR preview failed', err)
        this.previewSvg = ''
      }
    },
    animateCount(target) {
      // Smooth count-up on mount so the number feels arrived-at, not
      // just rendered. Skips work if 0 (no point animating to 0).
      cancelAnimationFrame(this._animFrame)
      if (!target) {
        this.animatedTotal = 0
        return
      }
      const start = performance.now()
      const duration = Math.min(900, 250 + target * 4)
      const easeOut = (t) => 1 - Math.pow(1 - t, 3)
      const step = (now) => {
        const t = Math.min(1, (now - start) / duration)
        this.animatedTotal = Math.round(easeOut(t) * target)
        if (t < 1) this._animFrame = requestAnimationFrame(step)
      }
      this._animFrame = requestAnimationFrame(step)
    },
    slugify(value) {
      return String(value || 'campaign')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')
        .slice(0, 60) || 'campaign'
    },
    async downloadPng() {
      if (!this.qrUrl) return
      this.downloading = true
      try {
        const QRCode = (await import('qrcode')).default
        // High error-correction so the printed code survives scuffs,
        // small prints, or partial occlusion (logo overlay later).
        const dataUrl = await QRCode.toDataURL(this.qrUrl, {
          errorCorrectionLevel: 'H',
          margin: 4,
          width: 1024,
          color: { dark: '#1D2549', light: '#ffffff' },
        })
        this.triggerDownload(dataUrl, `${this.slugify(this.campaign.name)}-qr.png`)
      } catch (err) {
        console.error('CampaignQrPanel: PNG download failed', err)
      } finally {
        this.downloading = false
      }
    },
    async downloadSvg() {
      if (!this.qrUrl) return
      try {
        const QRCode = (await import('qrcode')).default
        const svg = await QRCode.toString(this.qrUrl, {
          type: 'svg',
          errorCorrectionLevel: 'H',
          margin: 4,
          width: 1024,
          color: { dark: '#1D2549', light: '#ffffff' },
        })
        const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        this.triggerDownload(url, `${this.slugify(this.campaign.name)}-qr.svg`)
        // Revoke after a tick so the browser has the URL long enough to
        // initiate the download.
        setTimeout(() => URL.revokeObjectURL(url), 1000)
      } catch (err) {
        console.error('CampaignQrPanel: SVG download failed', err)
      }
    },
    triggerDownload(href, filename) {
      const a = document.createElement('a')
      a.href = href
      a.download = filename
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },
    async copyLink() {
      const url = this.shareUrl
      if (!url) return
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(url)
        } else {
          const textarea = document.createElement('textarea')
          textarea.value = url
          textarea.style.position = 'fixed'
          textarea.style.opacity = '0'
          document.body.appendChild(textarea)
          textarea.select()
          document.execCommand('copy')
          document.body.removeChild(textarea)
        }
        this.justCopied = true
        clearTimeout(this._copyTimeout)
        this._copyTimeout = setTimeout(() => { this.justCopied = false }, 2000)
      } catch (err) {
        console.error('CampaignQrPanel: copy failed', err)
      }
    },
  },
}
</script>

<style scoped>
/* ─── Outer panel ──────────────────────────────────────────────
   V2 card vocabulary — flat: solid white, 1px border, 16px radius,
   no box-shadow (matches V2About story cards). */
.qr-panel {
  position: relative;
  background: #ffffff;
  border: 1px solid var(--sideout-border, rgba(29, 37, 73, 0.08));
  border-radius: 16px;
  padding: 28px 28px 24px;
  font-family: 'Red Hat Display', system-ui, sans-serif;
  color: var(--sideout-navy, #1D2549);
  overflow: hidden;
}

.qr-panel__accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg,
    transparent 0%,
    var(--sideout-pink, #DA1884) 18%,
    #ff4fa5 50%,
    var(--sideout-pink, #DA1884) 82%,
    transparent 100%);
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  pointer-events: none;
}

/* ─── Header ───────────────────────────────────────────────── */
.qr-panel__header {
  margin-bottom: 24px;
  max-width: 60ch;
}
.qr-panel__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--sideout-pink, #DA1884);
  margin-bottom: 10px;
}
.qr-panel__eyebrow-pulse {
  position: relative;
  width: 7px;
  height: 7px;
  border-radius: 9999px;
  background: var(--sideout-pink, #DA1884);
  box-shadow: 0 0 0 4px rgba(218, 24, 132, 0.14);
}
.qr-panel__eyebrow-pulse::before {
  content: '';
  position: absolute;
  inset: -3px;
  border-radius: 9999px;
  background: var(--sideout-pink, #DA1884);
  opacity: 0.4;
  animation: qr-pulse 2.4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
@keyframes qr-pulse {
  0%   { transform: scale(0.4); opacity: 0.6; }
  70%  { transform: scale(1.6); opacity: 0; }
  100% { transform: scale(1.6); opacity: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .qr-panel__eyebrow-pulse::before { animation: none; }
}

.qr-panel__title {
  font-size: clamp(20px, 2.4vw, 26px);
  font-weight: 900;
  line-height: 1.15;
  letter-spacing: -0.015em;
  margin: 0 0 8px;
  color: var(--sideout-navy, #1D2549);
}
.qr-panel__desc {
  font-family: 'Red Hat Text', 'Red Hat Display', system-ui, sans-serif;
  font-size: 14px;
  line-height: 1.55;
  color: rgba(29, 37, 73, 0.7);
  margin: 0;
}

/* ─── Body grid ────────────────────────────────────────────── */
.qr-panel__body {
  display: grid;
  grid-template-columns: minmax(220px, 280px) 1fr;
  gap: 32px;
  align-items: start;
}
@media (max-width: 720px) {
  .qr-panel__body {
    grid-template-columns: 1fr;
    gap: 24px;
  }
}

/* ─── QR preview ───────────────────────────────────────────── */
.qr-panel__preview {
  position: relative;
  aspect-ratio: 1 / 1;
  background: #ffffff;
  border: 1px solid var(--sideout-border, rgba(29, 37, 73, 0.08));
  border-radius: 14px;
  padding: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.qr-panel__preview :deep(svg) {
  width: 100%;
  height: 100%;
  display: block;
}

/* ─── Stat column ──────────────────────────────────────────── */
.qr-panel__data {
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 0;
}
.qr-panel__stat {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.qr-panel__stat-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(29, 37, 73, 0.55);
}
.qr-panel__stat-value {
  display: inline-flex;
  align-items: baseline;
  gap: 10px;
  margin-top: 2px;
}
.qr-panel__stat-number {
  font-size: clamp(40px, 6vw, 56px);
  font-weight: 900;
  line-height: 1;
  letter-spacing: -0.025em;
  color: var(--sideout-navy, #1D2549);
  /* Tabular numerals — the digit positions stay stable as the count
     animates upward, so the layout doesn't jitter during the count-up. */
  font-variant-numeric: tabular-nums;
}
.qr-panel__stat-unit {
  font-family: 'Red Hat Text', 'Red Hat Display', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: rgba(29, 37, 73, 0.55);
  letter-spacing: 0.04em;
  text-transform: lowercase;
}

/* ─── Sparkline ────────────────────────────────────────────── */
.qr-panel__trend {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 10px;
}
.qr-panel__sparkline {
  width: 220px;
  max-width: 100%;
  height: 40px;
  display: block;
  overflow: visible;
}
.qr-panel__sparkline-stroke {
  stroke-dasharray: 600;
  stroke-dashoffset: 600;
  animation: qr-spark-in 1100ms cubic-bezier(0.22, 1, 0.36, 1) 200ms forwards;
}
@keyframes qr-spark-in {
  to { stroke-dashoffset: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .qr-panel__sparkline-stroke { animation: none; stroke-dashoffset: 0; }
}
.qr-panel__trend-caption {
  font-family: 'Red Hat Text', 'Red Hat Display', system-ui, sans-serif;
  font-size: 12.5px;
  color: rgba(29, 37, 73, 0.6);
  letter-spacing: 0.01em;
}

/* ─── Action buttons ───────────────────────────────────────────
   V2 button convention (matches V2About.vue): pill shape, solid
   color, font-weight 600, no gradient, no shadow. Hover = opacity
   shift for primary, fill swap for secondary. Active scales down. */
.qr-panel__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.qr-panel__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1;
  border-radius: 9999px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, opacity 0.15s, border-color 0.15s, transform 0.15s;
  white-space: nowrap;
}
.qr-panel__btn--primary {
  background-color: var(--sideout-pink, #DA1884);
  color: #ffffff;
}
.qr-panel__btn--primary:hover:not([disabled]) { opacity: 0.9; }
.qr-panel__btn--primary:active:not([disabled]) { transform: scale(0.98); }
.qr-panel__btn--primary[disabled] {
  opacity: 0.6;
  cursor: progress;
}
.qr-panel__btn--secondary {
  background-color: transparent;
  color: var(--sideout-navy, #1D2549);
  border-color: var(--sideout-navy, #1D2549);
  padding: 10px 22px;
}
.qr-panel__btn--secondary:hover {
  background-color: var(--sideout-navy, #1D2549);
  color: #ffffff;
}
.qr-panel__btn--secondary:active { transform: scale(0.98); }
.qr-panel__btn--secondary.is-copied {
  background-color: #16A34A;
  border-color: #16A34A;
  color: #ffffff;
}

/* ─── Channel chip strip ───────────────────────────────────── */
.qr-panel__channels {
  list-style: none;
  padding: 16px 0 0;
  margin: 4px 0 0;
  display: flex;
  flex-wrap: wrap;
  gap: 6px 10px;
  border-top: 1px dashed rgba(29, 37, 73, 0.12);
  font-family: 'Red Hat Text', 'Red Hat Display', system-ui, sans-serif;
}
.qr-panel__channels li {
  position: relative;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(29, 37, 73, 0.55);
  padding-left: 14px;
}
.qr-panel__channels li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 6px;
  height: 6px;
  border-radius: 2px;
  background: var(--sideout-pink, #DA1884);
  opacity: 0.55;
}

/* ─── Mobile refinement ────────────────────────────────────── */
@media (max-width: 540px) {
  .qr-panel { padding: 22px 18px 18px; border-radius: 14px; }
  .qr-panel__preview { max-width: 220px; margin: 0 auto; }
  .qr-panel__title { font-size: 18px; }
  .qr-panel__desc { font-size: 13px; }
  .qr-panel__actions { gap: 8px; }
  .qr-panel__btn { padding: 10px 18px; font-size: 13px; }
  .qr-panel__btn--secondary { padding: 8px 16px; }
}
</style>
